import { type ReactNode, useEffect } from 'react';

/**
 * Right-side slide-in drawer — used for edit flows.
 * Same prop shape as Modal so the two are interchangeable. Slide is
 * Tailwind transform-only (transition-transform); no new keyframes.
 */
export function Drawer({
  open,
  title,
  onClose,
  children,
  footer,
}: {
  readonly open: boolean;
  readonly title: string;
  readonly onClose: () => void;
  readonly children: ReactNode;
  readonly footer?: ReactNode;
}): JSX.Element {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('keydown', onKey); };
  }, [open, onClose]);

  return (
    <div
      className={[
        'fixed inset-0 z-50',
        open ? 'pointer-events-auto' : 'pointer-events-none',
      ].join(' ')}
      aria-hidden={!open}
    >
      <div
        className={[
          'absolute inset-0 bg-black/60 transition-opacity duration-200',
          open ? 'opacity-100' : 'opacity-0',
        ].join(' ')}
        onClick={onClose}
        role="presentation"
      />
      <aside
        className={[
          'absolute top-0 right-0 h-full w-full max-w-md sm:max-w-lg',
          'bg-bg-surface border-l border-bg-line shadow-2xl',
          'flex flex-col transition-transform duration-200',
          open ? 'translate-x-0' : 'translate-x-full',
        ].join(' ')}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <header className="flex items-center justify-between px-5 py-3 border-b border-bg-line shrink-0">
          <h2 className="text-sm font-mono text-ink">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-ink-muted hover:text-ink text-lg leading-none w-7 h-7 flex items-center justify-center rounded hover:bg-bg-raised"
            aria-label="Close"
          >
            ×
          </button>
        </header>
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {children}
        </div>
        {footer !== undefined && (
          <footer className="px-5 py-3 border-t border-bg-line flex items-center justify-end gap-2 shrink-0">
            {footer}
          </footer>
        )}
      </aside>
    </div>
  );
}
